import { Tuple } from '@mantine/core';
import CustomField from '@models/CustomField.model';
// Todo: Separate headings from children.
export interface IHeadingChildren extends Omit<IHeading, 'children'> {
  parentId: string;
  /** either the name of the child or the input type from customFields */
  inputType: InputType;
}
export interface IHeading {
  id: string;
  name: string;
  children: Map<string, IHeadingChildren>;
  nextId: string | null;
  prevId: string | null;
}

const headings = [
  [
    'Critical Data',
    [
      'Identification',
      'Firmware & Software',
      'Purchase Information',
      'Ownership',
      'License',
      'Assignee',
    ],
  ],
  ['Custom Data', [] as FieldHeadingId[]],
  ['Tags', ['Assign Tags']],
  ['Status', ['Selection']],
] as const;

const mapCreated = create(headings);
const linkedHeadingsMap = linkHeadings(mapCreated);
const initialPagesEntries = linkHeadingChildren(linkedHeadingsMap);
export const initialPages: IHeadings = new Map(initialPagesEntries);

export const customFieldTypePrefix = `custom-type-` as const;
export const customFieldIdPrefix = `custom-field-` as const;
export type IHeadings = Map<string, IHeading>;
export type HeadingsT = typeof headings[number][0];
export type ChildrenT = typeof headings[number][1][number];
export type FieldTypes = `${typeof customFieldTypePrefix}${CustomFieldTypes}`;
type FieldHeadingId = `${typeof customFieldIdPrefix}${string}`;
type InputType = FieldTypes | Exclude<ChildrenT, FieldHeadingId>;
type CustomFieldTypes = CustomField['type'];

/**
 * Creates headings without any links.
 */
function create(headingList: typeof headings): IHeadings {
  // make heading entries
  const headingEntries = headingList.map(heading => {
    const [id, children] = heading;
    const [nextHeading, prevHeading] = [null, null] as Tuple<null | string, 2>;

    // Make child entries
    const childrenEntries = children.map((child, _index, childArray) => {
      const { next, prev } = getNextPrev(childArray, _index);

      const childT = child as Exclude<typeof child, FieldHeadingId>;
      return [child, createHeadingChild(childT, id, next, prev)] as [
        string,
        IHeadingChildren,
      ];
    });

    // Link children to parent.
    const childrenMap = new Map(childrenEntries);
    return [id, createHeading(id, childrenMap, nextHeading, prevHeading)] as [
      HeadingsT,
      IHeading,
    ];
  });

  return new Map(headingEntries);
}

export function linkHeadings(createdMap: IHeadings) {
  const headingEntries = Array.from(createdMap);

  const linkedHeadings = headingEntries.map((entry, index, parentArray) => {
    let [nextId, prevId] = [null, null] as Tuple<null | string, 2>;
    const [key, value] = entry;
    if (parentArray.at(index + 1)) {
      [nextId] = parentArray[index + 1];
    }
    if (index !== 0 && parentArray.at(index - 1)) {
      [prevId] = parentArray[index - 1];
    }
    return [key, { ...value, nextId, prevId }] as [string, IHeading];
  });
  // Link the headings
  return new Map(linkedHeadings);
}
export function linkHeadingChildren(headingEntries: IHeadings) {
  const mapToSet = new Map(headingEntries);

  headingEntries.forEach(entry => {
    // get your last one
    let thisHead = entry;
    const thisChildren = Array.from(thisHead.children);
    let [, thisLastChild] = thisChildren.at(thisChildren.length - 1) ?? [];

    // get the next non-empty heading, if it exists.
    let nextNonEmpty: IHeading | undefined = thisHead;
    do {
      nextNonEmpty = mapToSet.get(nextNonEmpty?.nextId ?? '');
    } while (nextNonEmpty?.children.size === 0);

    // point your last to its first and vice versa if both exist, and reconstruct its children and set it
    // don't do to self
    let isNotSelf = nextNonEmpty !== thisHead;
    if (isNotSelf && nextNonEmpty && thisLastChild) {
      const nextChildren = Array.from(nextNonEmpty.children);
      let [, first] = nextChildren[0];

      thisLastChild = { ...thisLastChild, nextId: first.id };
      first = { ...first, prevId: thisLastChild.id };
      // set the next heading and yours
      nextChildren[0] = [first.id, first];
      thisChildren[thisChildren.length - 1] = [thisLastChild.id, thisLastChild];
      nextNonEmpty = { ...nextNonEmpty, children: new Map(nextChildren) };
      mapToSet.set(nextNonEmpty.id, nextNonEmpty);
    }

    // Do the same , in reverse for prev
    // Get your first child
    let [, thisFirstChild] = thisChildren.at(0) ?? [];

    //  get the first non-empty previous heading, if it exists
    let prevNonEmpty: IHeading | undefined = thisHead;
    do {
      prevNonEmpty = mapToSet.get(prevNonEmpty?.prevId ?? '');
    } while (prevNonEmpty?.children.size === 0);

    // point your first to its last and vice versa if both exist, and reconstruct its children and set it
    isNotSelf = prevNonEmpty !== thisHead;
    if (isNotSelf && prevNonEmpty && thisFirstChild) {
      const prevChildren = Array.from(prevNonEmpty.children);
      let [, last] = prevChildren[prevChildren.length - 1];
      thisFirstChild = { ...thisFirstChild, prevId: last.id };
      last = { ...last, nextId: thisFirstChild.id };

      // set
      prevChildren[prevChildren.length - 1] = [last.id, last];
      thisChildren[0] = [thisFirstChild.id, thisFirstChild];
      prevNonEmpty = { ...prevNonEmpty, children: new Map(prevChildren) };
      mapToSet.set(prevNonEmpty.id, prevNonEmpty);
    }

    // set your children, and you
    thisHead = { ...thisHead, children: new Map(thisChildren) };
    mapToSet.set(thisHead.id, thisHead);
  });
  return mapToSet;
}

// helpers
export function createHeading(
  id: string,
  childrenMap: Map<string, IHeadingChildren>,
  nextHeading: string | null,
  prevHeading: string | null,
): IHeading {
  return {
    id,
    name: id,
    children: childrenMap,
    nextId: nextHeading,
    prevId: prevHeading,
  };
}

function createHeadingChild(
  child: InputType,
  parentId: string,
  nextId: string | null,
  prevId: string | null,
): IHeadingChildren {
  return {
    id: child,
    name: child,
    parentId,
    inputType: child,
    nextId,
    prevId,
  };
}

export function getNextPrev<T>(childArray: readonly T[], index: number) {
  const next = childArray.at(index + 1) ?? null;
  const prev = index === 0 ? null : childArray[index - 1]; // must never fail.
  return { next, prev };
}
